(function () {
  if (customElements.get('subscription-toggle')) return;

  /**
   * Switches between sections (e.g. Build Order cups / beans) by hiding every
   * target except the active one. Hiding goes through a <style> rule on the
   * section wrapper, rendered server-side for the initial state, so the
   * section's own spacing disappears with it.
   */
  class SubscriptionToggle extends HTMLElement {
    connectedCallback() {
      /** @type {HTMLButtonElement[]} */
      this.options = Array.from(this.querySelectorAll('.subscription-toggle__option'));
      if (this.options.length === 0) return;

      this.styleEl = this.querySelector('style[data-subscription-toggle-style]');
      if (!this.styleEl) {
        this.styleEl = document.createElement('style');
        this.styleEl.setAttribute('data-subscription-toggle-style', '');
        this.prepend(this.styleEl);
      }

      this.options.forEach((option) => {
        option.addEventListener('click', () => this.select(option, { reveal: false }));
      });

      // Keeps existing anchor links (e.g. #build-order-coffiecups) working:
      // the browser cannot scroll to a hidden section, so show it first.
      this.onHashChange = () => {
        const option = this.optionFor(window.location.hash.slice(1));
        if (option) this.select(option, { reveal: true });
      };
      window.addEventListener('hashchange', this.onHashChange);
      this.onHashChange();

      // Theme editor: selecting a hidden target section makes it visible.
      this.onSectionSelect = (/** @type {Event} */ event) => {
        const section = /** @type {HTMLElement} */ (event.target);
        const option = this.options.find((o) => section.querySelector(`#${CSS.escape(o.dataset.target || '')}`));
        if (option) this.select(option, { reveal: false });
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
    }

    disconnectedCallback() {
      if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
      if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
    }

    /**
     * @param {string} id
     * @returns {HTMLButtonElement | undefined}
     */
    optionFor(id) {
      return id ? this.options.find((option) => option.dataset.target === id) : undefined;
    }

    /**
     * @param {HTMLButtonElement} active
     * @param {{ reveal: boolean }} options
     */
    select(active, { reveal }) {
      const changed = active.getAttribute('aria-pressed') !== 'true';

      this.options.forEach((option) => {
        const isActive = option === active;
        option.setAttribute('aria-pressed', String(isActive));
        option.classList.toggle('button', isActive);
        option.classList.toggle('button-secondary', !isActive);
      });

      const hidden = this.options
        .filter((option) => option !== active && option.dataset.target)
        .map((option) => `.shopify-section:has(#${CSS.escape(option.dataset.target || '')})`);
      if (this.styleEl) this.styleEl.textContent = hidden.length ? `${hidden.join(',')} { display: none; }` : '';

      // Visibility belongs to the style rule above; drop inline display that
      // other scripts may have set on the section wrappers.
      this.options.forEach((option) => {
        const el = document.getElementById(option.dataset.target || '');
        const wrapper = /** @type {HTMLElement | null} */ (el?.closest('.shopify-section') || null);
        wrapper?.style.removeProperty('display');
      });

      const target = document.getElementById(active.dataset.target || '');
      if (!target) return;

      if (changed) {
        target.classList.remove('subscription-toggle-target--entering');
        void target.offsetWidth;
        target.classList.add('subscription-toggle-target--entering');
      }
      if (reveal) target.scrollIntoView({ block: 'start' });
    }
  }

  customElements.define('subscription-toggle', SubscriptionToggle);
})();
